import SEOHead from "@/components/base/SEOHead";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppFloat from "@/components/feature/WhatsAppFloat";
import Breadcrumb from "@/components/feature/Breadcrumb";
import TrustBannerSection from "@/components/feature/TrustBannerSection";

const guias = [
  {
    slug: "como-cambiar-cinta-persiana",
    icon: "ri-scissors-cut-line",
    title: "Cómo cambiar la cinta de una persiana",
    desc: "Guía completa sobre el cambio de cintas de persiana: cuándo cambiarla, tipos de cinta, proceso paso a paso y cuánto cuesta en Málaga.",
  },
  {
    slug: "como-cambiar-cuerda-persiana",
    icon: "ri-link-unlink",
    title: "Cómo cambiar la cuerda de una persiana",
    desc: "Sistemas de cuerda vs cinta: diferencias, causas de rotura, cómo arreglar la cuerda de una persiana y coste de reparación.",
  },
  {
    slug: "persiana-no-sube-ni-baja",
    icon: "ri-arrow-up-down-line",
    title: "Mi persiana no sube ni baja: causas y solución",
    desc: "¿Tu persiana se ha quedado bloqueada? Las 5 causas más frecuentes, cómo identificarlas y qué hacer para que vuelva a funcionar.",
  },
  {
    slug: "persiana-atascada-descolgada",
    icon: "ri-error-warning-line",
    title: "Persiana atascada o descolgada: cómo arreglarla",
    desc: "Por qué se atasca una persiana, tipos de atasco según el material, cómo liberarla sin dañarla y cuándo llamar a un profesional.",
  },
  {
    slug: "reparar-recogedor-persiana",
    icon: "ri-uninstall-line",
    title: "Cómo cambiar o reparar el recogedor de la persiana",
    desc: "El recogedor es la pieza que más sufre. Cómo saber si está roto, cuándo repararlo o cambiarlo, y precio de la reparación.",
  },
  {
    slug: "cambiar-motor-persiana",
    icon: "ri-settings-3-line",
    title: "Cambiar el motor de una persiana eléctrica",
    desc: "Cómo saber si el motor está averiado, qué modelos hay, cuánto cuesta cambiarlo y cómo hacer que dure más años. Motores Somfy y Nice.",
  },
  {
    slug: "cambiar-lamas-persiana",
    icon: "ri-stack-line",
    title: "Cómo cambiar las lamas rotas de una persiana",
    desc: "Lamas de PVC, aluminio y madera: por qué se rompen, cómo sustituir solo las dañadas sin cambiar toda la persiana, y precio por lama.",
  },
];

export default function GuiasIndex() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="Guías de Reparación de Persianas | Persianas Málaga"
        description="Guías de reparación de persianas en Málaga: cambio de cintas, cuerdas, recogedores, motores, lamas rotas y solución de averías frecuentes. Presupuesto gratis."
      />
      <Navbar />

      <Breadcrumb items={[
        { label: "Inicio", path: "/" },
        { label: "Guías" },
      ]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Guías de reparación de persianas en Málaga",
        "description": "Guías prácticas sobre reparación de persianas: cambio de cintas, cuerdas, recogedores, motores, lamas y solución de averías frecuentes. Servicio local en Málaga.",
        "mainEntity": {
          "@type": "ItemList",
          "itemListElement": guias.map((g, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": { "@type": "Article", "name": g.title, "url": `https://persianas-malaga.es/guias/${g.slug}` }
          }))
        }
      }) }} />

      <section className="py-14 md:py-20 bg-gradient-to-br from-background-50 via-primary-50/30 to-accent-50/30">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4 font-heading">
              Guías de reparación de <span className="text-primary-500">persianas</span>
            </h1>
            <p className="text-sm md:text-lg text-foreground-600 max-w-2xl mx-auto">
              Hemos preparado estas guías para ayudarte a entender qué le pasa a tu persiana y cómo solucionarlo. Si estás en Málaga, llámanos y lo reparamos nosotros.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {guias.map((g) => (
              <Link
                key={g.slug}
                to={`/guias/${g.slug}`}
                className="group p-5 md:p-6 bg-background-100 rounded-2xl border border-background-200/60 hover:border-primary-300/60 hover:-translate-y-0.5 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-primary-500/15 to-accent-500/15 text-primary-600 mb-4">
                  <span className="w-5 h-5 flex items-center justify-center"><i className={`${g.icon} text-lg`}></i></span>
                </div>
                <h2 className="text-sm md:text-base font-bold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">{g.title}</h2>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{g.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
          <h3 className="text-xl md:text-2xl font-bold text-background-50 mb-3 font-heading">¿Prefieres que lo hagamos nosotros?</h3>
          <p className="text-sm md:text-base text-background-50/80 mb-6">Presupuesto gratis · Sin desplazamiento · Respuesta en 24-48h · 2 años de garantía</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href="tel:+34951120770" className="inline-flex items-center justify-center gap-2 bg-background-50 hover:bg-background-100 text-primary-600 font-semibold text-sm px-8 py-4 rounded-full transition-all whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill text-base"></i></span>
              951 12 07 70
            </a>
            <Link to="/contacto" className="inline-flex items-center justify-center gap-2 bg-background-50/20 hover:bg-background-50/30 text-background-50 font-semibold text-sm px-8 py-4 rounded-full border border-background-50/30 transition-all whitespace-nowrap cursor-pointer">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-base"></i></span>
              Formulario de contacto
            </Link>
          </div>
        </div>
      </section>

      <TrustBannerSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}